import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { bugsnagClient } from "bugsnag";
import type { NotifiableError } from "@bugsnag/js";
import { useRequest } from "hooks/useRequest";
import api from "utils/api";
import {
  pixelsToMeters,
  buildEdgeListTopology,
} from "utils/math/findTheShortestCablePath/utils";
import { findTheShortestCablePath } from "utils/math/findTheShortestCablePath";
import { VERTICAL_DROP_PER_CIRCUIT } from "utils/math/findTheShortestCablePath/constants";
import { getCableInfo } from "./utils";
import type {
  CablePathState,
  FetchCableInfo,
  CalculateShortestPathArgs,
} from "./types";

export const useElementCablePath = () => {
  const { t } = useTranslation();
  const [cablePathState, setCablePathState] = useState<CablePathState>({});

  const resetCablePathState = useCallback(() => {
    setCablePathState({});
  }, []);

  const resetShortestPath = useCallback(() => {
    setCablePathState((prev) => ({ ...prev, shortestPath: undefined }));
  }, []);

  const fetchCableInfo: FetchCableInfo = useCallback(async (dwgId) => {
    try {
      const cable = await api.getMultiLineCableByDwgId(dwgId);
      if (!cable) return;

      setCablePathState((prev) => ({
        ...prev,
        cableInfo: getCableInfo(cable),
      }));
    } catch (error) {
      bugsnagClient.notify(error as NotifiableError);
    }
  }, []);

  const {
    run: calculateShortestPath,
    isLoading: isCalculatingShortestPath,
    error: shortestPathError,
  } = useRequest(
    async ({
      floorPlanId,
      floorPlanPaths,
      startNode,
    }: CalculateShortestPathArgs) => {
      try {
        const routes =
          floorPlanPaths ?? (await api.getFloorPlanGraph(floorPlanId));

        if (!routes || routes.nodes.length <= 0 || routes.edges.length <= 0) {
          toast.warning(t("shortestPath.errors.noCableTracesDetected"));
          return;
        }

        const cable = await api.getMultiLineCableByDwgId(startNode.dwgId);
        if (!cable) {
          toast.warning(t("shortestPath.errors.noDistributionBoardInfo"));
          return;
        }

        const destination = { ...cable, x: cable.sourceX, y: cable.sourceY };

        const trayNetwork = buildEdgeListTopology(routes.nodes, routes.edges);

        const circuit = findTheShortestCablePath({
          circuitId: "single-destination",
          circuitData: {
            destinations: [
              {
                element_id: destination.id,
                element_name: destination.destination,
                coordinates: { x: destination.x, y: destination.y },
              },
            ],
            sld_element: {
              breaker: destination.breaker,
              cable_spec: destination.cable_spec,
              destination: destination.destination,
            },
          },
          source: startNode,
          topology: trayNetwork,
          sourceId: String(startNode.id),
        });

        const firstDestination = circuit.destinations[0];

        if (!firstDestination?.reachable || !firstDestination?.path_length) {
          toast.error(t("shortestPath.errors.failedToFindPath"));
          return;
        }

        const trayMeters = pixelsToMeters(firstDestination.path_length);
        const verticalMeters = VERTICAL_DROP_PER_CIRCUIT;
        const totalMeters = trayMeters + verticalMeters;

        setCablePathState({
          shortestPath: {
            waypoints: firstDestination.path_coords,
            distanceBreakdown: {
              trayMeters,
              verticalMeters,
              totalMeters,
            },
          },
          cableInfo: {
            ...getCableInfo(cable),
            destinations: {
              reachable: 1,
              total: 1,
            },
          },
        });
      } catch (error) {
        toast.error(t("shortestPath.errors.failedToFindPath"));
        bugsnagClient.notify(error as NotifiableError);
      }
    },
    { manual: true },
  );

  return {
    cablePathState,
    resetCablePathState,
    resetShortestPath,
    calculateShortestPath,
    fetchCableInfo,
    isCalculatingShortestPath,
    shortestPathError,
  };
};
